import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usePartyFilter } from '../context/PartyFilterContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatEuro, formatPercent, formatNumber, getGreeting, formatDate } from '../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { 
  TrendingUp, 
  Building2, 
  CreditCard, 
  Users, 
  ArrowRight,
  PiggyBank,
  Landmark,
  Leaf
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Market standard energy label colors: Green (A) to Red (G)
const ENERGY_LABEL_COLORS = {
  'A++++': '#009036',
  'A+++': '#009036',
  'A++': '#009036',
  'A+': '#52b946',
  'A': '#c8d300',
  'B': '#fff200',
  'C': '#edb731',
  'D': '#d66f1e',
  'E': '#cc232a',
  'F': '#cc232a',
  'G': '#cc232a',
  'N/A': '#64748b'
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { selectedPartyId } = usePartyFilter();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKPIs();
  }, [selectedPartyId]);

  const fetchKPIs = async () => {
    try {
      const params = selectedPartyId !== 'all' ? `?party_id=${selectedPartyId}` : '';
      const response = await axios.get(`${API}/dashboard/kpis${params}`, { withCredentials: true });
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const chartData = kpis?.loans_by_year 
    ? Object.entries(kpis.loans_by_year)
        .map(([year, amount]) => ({ year, amount }))
        .sort((a, b) => a.year.localeCompare(b.year))
    : [];

  const chartColors = ['#b4976f', '#264162', '#c8b599', '#64748b', '#94a3b8'];

  // Energy label pie chart data
  const energyData = kpis?.sustainability_kpis?.energy_distribution
    ? Object.entries(kpis.sustainability_kpis.energy_distribution)
        .map(([label, count]) => ({ name: label, value: count, fill: ENERGY_LABEL_COLORS[label] || '#64748b' }))
    : [];

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-32 bg-slate-800" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Comfortaa']">
              {getGreeting(language)}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-400 mt-1 text-sm sm:text-base">
              {t('dashboard')} - {new Date().toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button 
              className="bg-[#264162] hover:bg-[#1e344f] flex-1 sm:flex-none text-sm sm:text-base"
              onClick={() => navigate('/loans')}
              data-testid="add-loan-btn"
            >
              <CreditCard className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t('addLoan')}</span>
              <span className="sm:hidden">Lening</span>
            </Button>
            <Button 
              className="bg-[#b4976f] hover:bg-[#a38663] flex-1 sm:flex-none text-sm sm:text-base"
              onClick={() => navigate('/realestate')}
              data-testid="add-property-btn"
            >
              <Building2 className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t('addProperty')}</span>
              <span className="sm:hidden">Vastgoed</span>
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 kpi-grid">
          <Card className="bg-[#1e293b] border-slate-700 kpi-card">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-2 rounded-lg bg-[#b4976f]/20">
                  <CreditCard className="h-5 w-5 text-[#b4976f]" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('loans')}</p>
                  <p className="text-2xl font-bold text-white">{kpis?.loan_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1e293b] border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#264162]/50">
                  <Building2 className="h-5 w-5 text-[#c8b599]" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('realEstate')}</p>
                  <p className="text-2xl font-bold text-white">{kpis?.property_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1e293b] border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Users className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('tenants')}</p>
                  <p className="text-2xl font-bold text-white">{kpis?.tenant_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1e293b] border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <PiggyBank className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">{t('surplus')}</p>
                  <p className="text-2xl font-bold text-white">{formatEuro(kpis?.finance_kpis?.surplus)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Finance Key Figures */}
          <Card className="bg-[#1e293b] border-slate-700 kpi-card">
            <CardHeader>
              <CardTitle className="text-xl text-white font-['Comfortaa'] flex items-center gap-2">
                <Landmark className="h-5 w-5 text-[#b4976f]" />
                {t('financeKeyFigures')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('totalLoanAmount')}</span>
                  <span className="text-lg font-semibold text-[#b4976f] euro-amount">
                    {formatEuro(kpis?.finance_kpis?.total_loan_amount)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('loanToValue')}</span>
                  <span className="text-lg font-semibold text-white">
                    {formatPercent(kpis?.finance_kpis?.ltv)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">ICR</span>
                  <span className="text-lg font-semibold text-white">
                    {formatNumber(kpis?.finance_kpis?.icr, 2)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">DSCR</span>
                  <span className="text-lg font-semibold text-white">
                    {formatNumber(kpis?.finance_kpis?.dscr, 2)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('debtYield')}</span>
                  <span className="text-lg font-semibold text-white">
                    {formatPercent(kpis?.finance_kpis?.debt_yield)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-400">{t('surplus')}</span>
                  <span className={`text-lg font-semibold ${kpis?.finance_kpis?.surplus >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatEuro(kpis?.finance_kpis?.surplus)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Real Estate Key Figures */}
          <Card className="bg-[#1e293b] border-slate-700 kpi-card">
            <CardHeader>
              <CardTitle className="text-xl text-white font-['Comfortaa'] flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#b4976f]" />
                {t('realEstateKeyFigures')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('totalMarketValue')}</span>
                  <span className="text-lg font-semibold text-[#b4976f] euro-amount">
                    {formatEuro(kpis?.realestate_kpis?.total_market_value)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('totalGrossRental')}</span>
                  <span className="text-lg font-semibold text-white euro-amount">
                    {formatEuro(kpis?.realestate_kpis?.total_gross_rental)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('totalNetRental')}</span>
                  <span className="text-lg font-semibold text-white euro-amount">
                    {formatEuro(kpis?.realestate_kpis?.total_net_rental)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">{t('vacancyRate')}</span>
                  <span className={`text-lg font-semibold ${kpis?.realestate_kpis?.vacancy_rate > 10 ? 'text-amber-400' : 'text-white'}`}>
                    {formatPercent(kpis?.realestate_kpis?.vacancy_rate)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-700">
                  <span className="text-slate-400">WALE</span>
                  <span className="text-lg font-semibold text-white">
                    {kpis?.realestate_kpis?.aggregated_wale?.wale_years != null 
                      ? `${kpis.realestate_kpis.aggregated_wale.wale_years} ${language === 'nl' ? 'jaar' : 'years'}`
                      : '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-400">Total m²</span>
                  <span className="text-lg font-semibold text-white">
                    {formatNumber(kpis?.realestate_kpis?.total_sqm)} m²
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sustainability KPIs */}
          <Card className="bg-[#1e293b] border-slate-700 kpi-card">
            <CardHeader>
              <CardTitle className="text-xl text-white font-['Comfortaa'] flex items-center gap-2">
                <Leaf className="h-5 w-5 text-emerald-400" />
                {t('sustainabilityKeyFigures')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {energyData.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={energyData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {energyData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-500">
                  {t('noData')}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {energyData.map((item) => (
                  <div key={item.name} className="flex items-center gap-1 text-xs">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></span>
                    <span className="text-slate-400">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Loans by Expiry Chart */}
        {chartData.length > 0 && (
          <Card className="bg-[#1e293b] border-slate-700">
            <CardHeader>
              <CardTitle className="text-xl text-white font-['Comfortaa'] flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#b4976f]" />
                {t('loansByExpiry')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="year" stroke="#94a3b8" />
                    <YAxis 
                      stroke="#94a3b8" 
                      tickFormatter={(value) => `€${(value / 1000000).toFixed(1)}M`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [formatEuro(value), 'Amount']}
                    />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top 5 Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top 5 Properties */}
          <Card className="bg-[#1e293b] border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white font-['Comfortaa']">
                {t('top5Properties')}
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                className="border-[#b4976f] text-[#b4976f] hover:bg-[#b4976f]/10"
                onClick={() => navigate('/realestate')}
                data-testid="view-all-properties-btn"
              >
                {t('view')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">{t('address')}</TableHead>
                    <TableHead className="text-slate-400 text-center">{t('energyLabel')}</TableHead>
                    <TableHead className="text-slate-400 text-right">{t('marketValue')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kpis?.top_properties?.length > 0 ? (
                    kpis.top_properties.map((prop, index) => (
                      <TableRow key={prop.realestate_id} className="border-slate-700 table-row-hover">
                        <TableCell className="text-white">
                          {index + 1}. {prop.street} {prop.house_number}
                        </TableCell>
                        <TableCell className="text-center">
                          <span 
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ 
                              backgroundColor: `${ENERGY_LABEL_COLORS[prop.best_energy_label] || '#64748b'}20`,
                              color: ENERGY_LABEL_COLORS[prop.best_energy_label] || '#64748b'
                            }}
                          >
                            {prop.best_energy_label || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-[#c8b599] euro-amount">
                          {formatEuro(prop.market_value)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                        {t('noData')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Top 5 Tenants */}
          <Card className="bg-[#1e293b] border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white font-['Comfortaa']">
                {t('top5Tenants')}
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                className="border-[#b4976f] text-[#b4976f] hover:bg-[#b4976f]/10"
                onClick={() => navigate('/realestate')}
                data-testid="view-all-tenants-btn"
              >
                {t('view')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">{t('tenantName')}</TableHead>
                    <TableHead className="text-slate-400 text-right">{t('endDate')}</TableHead>
                    <TableHead className="text-slate-400 text-right">{t('rentalAmount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kpis?.top_tenants?.length > 0 ? (
                    kpis.top_tenants.map((tenant, index) => (
                      <TableRow key={tenant.tenant_id} className="border-slate-700 table-row-hover">
                        <TableCell className="text-white">
                          {index + 1}. {tenant.tenant_name}
                        </TableCell>
                        <TableCell className="text-right text-slate-400">
                          {formatDate(tenant.end_date)}
                        </TableCell>
                        <TableCell className="text-right text-[#c8b599] euro-amount">
                          {formatEuro(tenant.rental_amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                        {t('noData')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
